'use client';

import {
  useState,
  useCallback,
  useMemo,
  useContext,
  useEffect,
  type FC,
} from 'react';
import Link from 'next/link';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  ErrorBoundary,
  Flex,
  LayoutContent,
  QueueStatus,
  SelectAllRows,
} from '@/_components';
import { MultiSelectActions } from './components/MultiSelectActions';
import { OverviewContext } from '@/_contexts/admin/OverviewProvider';
import { OverviewHeader } from './components/OverviewHeader';
import { OverviewTable } from './components/OverviewTable';
import { LinkedAccount } from '@/_types/models/linkedAccount';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { getAllQuestionnaires } from './AdminDashboard.utils';
import { LowQuestionnaireUsagePercentage } from './components/OverviewHeader/components/LowQuestionnaireUsagePercentage';
import { getLinkedAccounts } from '@/_api/users';
import { checkSharepointIntegration } from '@/_api/integrations';
import type { AdminDashboardProps } from './AdminDashboard.types';

export const AdminDashboard: FC<AdminDashboardProps> = () => {
  const {
    isLoading,
    loadQuestionnaires,
    onExportToggle,
    questionnaireCount,
    setLoading,
    setQuestionnaireExport,
    selectedQuestionnaireCount,
    selectedQuestionnaires,
    setSelectedQuestionnaires,
  } = useContext(QuestionnaireContext);
  const { filter } = useContext(OverviewContext);
  const { firm } = useContext(FirmSettingsContext);

  const [disconnectedAccounts, setDisconnectedAccounts] = useState<
    LinkedAccount[]
  >([]);
  const [sharepointAccountIsDisconnected, setSharepointAccountIsDisconnected] =
    useState<boolean>(false);
  const [sharepointExpiringIn, setSharepointExpiringIn] = useState<
    string | null
  >(null);
  const { token } = useContext(UserContext);

  const onSelectAll = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const response = await getAllQuestionnaires(
        filter,
        questionnaireCount,
        token,
      );

      setSelectedQuestionnaires(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [
    filter,
    questionnaireCount,
    setLoading,
    token,
    setSelectedQuestionnaires,
  ]);

  const clearRowSelection = () => {
    setSelectedQuestionnaires([]);
  };

  useEffect(() => {
    const checkForDisconnectedAccounts = async () => {
      try {
        const { accounts } = await getLinkedAccounts({ token });
        const { firm_connected, firm_has_sharepoint, expiring_soon_in } =
          await checkSharepointIntegration({ token });

        const disconnectedAccounts: Array<LinkedAccount> = accounts?.filter(
          (account) => account.expired_at,
        );

        if (disconnectedAccounts.length === 0 && !firm_has_sharepoint) return;

        const disconnectedSharepointAccount = disconnectedAccounts.some(
          (account) => {
            if (account.provider !== 'microsoft') return false;
            if (
              !account.scoped_permissions ||
              account.scoped_permissions.length === 0
            ) {
              return false;
            }

            return account.scoped_permissions.some(
              (permission) => permission === 'Sites.ReadWrite.All',
            );
          },
        );

        // Check for SharePoint expiration status
        if (
          typeof expiring_soon_in === 'string' &&
          !disconnectedSharepointAccount
        ) {
          // Integration is expiring soon
          setSharepointExpiringIn(expiring_soon_in);
        } else if (
          disconnectedSharepointAccount ||
          (firm_has_sharepoint && !firm_connected)
        ) {
          setSharepointAccountIsDisconnected(true);
        } else if (disconnectedAccounts.length > 0) {
          setDisconnectedAccounts(disconnectedAccounts);
        }
      } catch (error) {
        console.error(error);
      }
    };

    checkForDisconnectedAccounts();

    return () => {
      setSharepointAccountIsDisconnected(false);
      setSharepointExpiringIn(null);
      setDisconnectedAccounts([]);
    };
  }, [token]);

  const onExport = () => {
    onExportToggle();
    setQuestionnaireExport(selectedQuestionnaires);
  };

  const alert = useMemo(() => {
    if (firm?.fallback_email_disconnected) {
      return (
        <Alert type="danger">
          <Flex isColumn gap={2}>
            <div>
              Your fallback email ({firm.default_sender_email_address}) has been
              disconnected. This could either be because the linked account
              associated with that email has been removed by one your firm users
              or because it has expired (Microsoft and Gmail accounts expire
              after 90 days).
            </div>
            <div>
              Please relink it, change your fallback email, or use
              noreply@soraban.com{' '}
              <Link href="/firm-settings" target="_blank">
                here
              </Link>
              .
            </div>
          </Flex>
        </Alert>
      );
    }

    if (disconnectedAccounts.length > 0) {
      const provider =
        disconnectedAccounts[0].provider &&
        disconnectedAccounts[0].provider.charAt(0).toUpperCase() +
          disconnectedAccounts[0].provider.slice(1) +
          ' ';

      return (
        <Alert type="danger">
          Your {provider}email integration is disconnected. Please unlink it and
          relink it{' '}
          <Link href="/admin-profile/integrations" target="_blank">
            here
          </Link>
          .
        </Alert>
      );
    }

    if (sharepointExpiringIn) {
      return (
        <Alert type="warning">
          Your SharePoint integration will expire in {sharepointExpiringIn}.
          Please re-authenticate to avoid any downtime. Instructions can be
          found{' '}
          <a
            href="https://helpcenter.soraban.com/sharepoint-setup-guide#setup-and-configuration"
            target="_blank"
          >
            here
          </a>
          .
        </Alert>
      );
    }

    if (sharepointAccountIsDisconnected) {
      return (
        <Alert type="danger">
          Your SharePoint integration is disconnected. Instructions on how to
          re-connect your integration can be found{' '}
          <a
            href="https://helpcenter.soraban.com/sharepoint-setup-guide#setup-and-configuration"
            target="_blank"
          >
            here
          </a>
          .
        </Alert>
      );
    }

    return null;
  }, [
    disconnectedAccounts,
    sharepointAccountIsDisconnected,
    sharepointExpiringIn,
    firm?.fallback_email_disconnected,
    firm?.default_sender_email_address,
  ]);

  return (
    <LayoutContent
      alert={alert}
      title="Collect"
      headerRight={<LowQuestionnaireUsagePercentage />}
    >
      <Card>
        <CardContent withTitle={true} isFullHeight={true}>
          <CardHeader>
            <OverviewHeader />
            <SelectAllRows
              isLoading={isLoading}
              onClearRowSelection={clearRowSelection}
              onSelectAll={onSelectAll}
              selectedCount={selectedQuestionnaireCount}
              totalCount={questionnaireCount}
            >
              <Button
                intent="primary"
                size="small"
                iconRight="download"
                isDisabled={isLoading}
                count={selectedQuestionnaireCount}
                onClick={onExport}
                value="Export questionnaires"
              />
              <MultiSelectActions
                isLoading={isLoading}
                loadQuestionnaires={loadQuestionnaires}
                selectedCount={selectedQuestionnaireCount}
                selectedQuestionnaires={selectedQuestionnaires}
                setSelectedQuestionnaires={setSelectedQuestionnaires}
                filter={filter}
              />
            </SelectAllRows>
          </CardHeader>
          <QueueStatus />
          <ErrorBoundary>
            <OverviewTable
              isLoading={isLoading}
              selectedQuestionnaires={selectedQuestionnaires}
              setSelectedQuestionnaires={setSelectedQuestionnaires}
            />
          </ErrorBoundary>
        </CardContent>
      </Card>
    </LayoutContent>
  );
};
